
import { supabase } from './supabase';

/**
 * Uploads an image to Supabase Storage and returns the public URL.
 * @param file The file to upload.
 * @param bucket The bucket name (default: 'expeditions').
 * @param path The folder path inside the bucket.
 */
/**
 * Automatically optimizes images in the browser before uploading.
 * Resizes images to max 2560px dimension and converts/compresses to high-quality JPEG.
 * Reduces upload time from seconds/minutes to milliseconds and prevents storage limit errors.
 */
export async function optimizeImageForWeb(file: File, maxWidth = 2560, quality = 0.85): Promise<File> {
    // Only optimize image types supported by browser canvas
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type.toLowerCase())) {
        return file;
    }

    // If file is already small (under 800KB), no need to re-compress
    if (file.size < 800 * 1024) {
        return file;
    }

    return new Promise((resolve) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            let { width, height } = img;

            // Only downscale if larger than maxWidth
            if (width > maxWidth || height > maxWidth) {
                if (width >= height) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                } else {
                    width = Math.round((width * maxWidth) / height);
                    height = maxWidth;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                resolve(file);
                return;
            }

            // Draw with smooth scaling
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob || blob.size >= file.size) {
                        // If compression didn't reduce size, keep original
                        resolve(file);
                        return;
                    }
                    const newFileName = file.name.replace(/\.[^.]+$/, '.jpg');
                    const optimizedFile = new File([blob], newFileName, {
                        type: 'image/jpeg',
                        lastModified: Date.now(),
                    });
                    resolve(optimizedFile);
                },
                'image/jpeg',
                quality
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(file);
        };

        img.src = objectUrl;
    });
}

/**
 * Uploads an image to Supabase Storage and returns the public URL.
 * @param file The file to upload.
 * @param bucket The bucket name (default: 'expeditions').
 * @param path The folder path inside the bucket.
 */
export async function uploadImage(file: File, bucket = 'expeditions', path = ''): Promise<string> {
    const nameLower = file.name.toLowerCase();

    // 1. Format validation
    if (nameLower.endsWith('.heic') || nameLower.endsWith('.heif')) {
        throw new Error(`La foto "${file.name}" está en formato HEIC de iPhone. Por favor conviértela o compártela como JPG antes de subirla.`);
    }
    if (nameLower.endsWith('.raw') || nameLower.endsWith('.cr2') || nameLower.endsWith('.nef') || nameLower.endsWith('.rw2') || nameLower.endsWith('.arw')) {
        throw new Error(`La foto "${file.name}" es un archivo RAW de cámara. Debes exportarla a formato JPG antes de subirla a la web.`);
    }

    // 2. Pre-upload client compression if it is a large web image
    let fileToUpload = file;
    try {
        fileToUpload = await optimizeImageForWeb(file);
    } catch (e) {
        console.warn('Image optimization skipped:', e);
    }

    // 3. Size limit check (max 25MB)
    if (fileToUpload.size > 25 * 1024 * 1024) {
        throw new Error(`La foto "${file.name}" supera el tamaño máximo permitido de 25MB. Intenta reducir su peso.`);
    }

    const fileExt = fileToUpload.name.split('.').pop() || 'jpg';
    const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt.toLowerCase()}`;
    const filePath = path ? `${path}/${fileName}` : fileName;

    const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(filePath, fileToUpload, {
            contentType: fileToUpload.type || 'image/jpeg',
            upsert: true
        });

    if (uploadError) {
        const msg = uploadError.message || '';
        if (msg.includes('exceeded the maximum allowed size') || msg.includes('413')) {
            throw new Error(`La foto "${file.name}" supera el límite de tamaño permitido por el servidor.`);
        }
        if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
            throw new Error(`Error de conexión al subir "${file.name}". La foto es muy pesada o tu internet se interrumpió.`);
        }
        throw new Error(`Error al subir "${file.name}": ${msg}`);
    }

    const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(filePath);

    return publicUrl;
}

/**
 * Deletes an image from Supabase Storage.
 * @param url The full public URL of the image.
 * @param bucket The bucket name.
 */
export async function deleteImage(url: string, bucket = 'expeditions'): Promise<void> {
    // Extract path from URL (this is a bit naive but works for standard Supabase URLs)
    const pathParts = url.split(`${bucket}/`);
    if (pathParts.length < 2) return;
    
    const filePath = decodeURIComponent(pathParts[1]);
    const { error } = await supabase.storage.from(bucket).remove([filePath]);
    
    if (error) {
        throw error;
    }
}
